import { useEffect, useMemo, type FC } from "react"

import { useQuery } from "@apollo/client"
import { Controller, useForm } from "react-hook-form"

import styles from "./KnownIssueForm.module.scss"

import { graphql } from "@/souhl_admin/gql/__generated__/gql"
import {
	knownIssueFormResolver,
	knownIssueFormSchema,
	logModuleNameValues,
	type KnownIssueFormData,
} from "@/souhl_admin/utils/forms/knownIssue"

const GetSlackMentionTargets = graphql(/* GraphQL */ `
	query GetSlackMentionTargets {
		slackMentionTargets {
			targets {
				id
				name
			}
		}
	}
`)

type KnownIssueFormProps = {
	initialData?: Partial<KnownIssueFormData>
	onSubmit: (data: KnownIssueFormData) => void
	submitLabel: string
	isSubmitting: boolean
	onCancel: () => void
}

const KnownIssueForm: FC<KnownIssueFormProps> = ({
	initialData,
	onSubmit,
	submitLabel,
	isSubmitting,
	onCancel,
}) => {
	const defaultValues = useMemo(() => knownIssueFormSchema.getDefault(), [])

	const {
		register,
		control,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<KnownIssueFormData>({
		resolver: knownIssueFormResolver,
		defaultValues: {
			...defaultValues,
			...initialData,
		},
	})

	const { data: mentionTargetsData } = useQuery(GetSlackMentionTargets)
	const mentionTargets = mentionTargetsData?.slackMentionTargets.targets ?? []

	useEffect(() => {
		if (initialData) {
			reset({
				...defaultValues,
				...initialData,
			})
		}
	}, [initialData, reset, defaultValues])

	// TODO: ここから下見ていない
	return (
		<form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
			<div className={styles.section}>
				<h3 className={styles.sectionTitle}>基本情報</h3>

				<div className={styles.field}>
					<label className={styles.label}>Description *</label>
					<textarea
						className={`${styles.textarea} ${errors.description ? styles.inputError : ""}`}
						{...register("description")}
						placeholder="Issue の説明"
						rows={3}
						disabled={isSubmitting}
					/>
					{errors.description && (
						<span className={styles.errorMessage}>
							{errors.description.message}
						</span>
					)}
				</div>

				<div className={styles.row}>
					<div className={styles.field}>
						<label className={styles.label}>Issue Type *</label>
						<select
							className={styles.select}
							{...register("issueType")}
							disabled={isSubmitting}
						>
							<option value="PROGRAMMING_ISSUE">Programming Issue</option>
							<option value="BIZ_TASK">Biz Task</option>
							<option value="DEV_TASK">Dev Task</option>
						</select>
					</div>

					<div className={styles.field}>
						<label className={styles.label}>Log Level *</label>
						<select
							className={styles.select}
							{...register("logLevel")}
							disabled={isSubmitting}
						>
							<option value="CRITICAL">CRITICAL</option>
							<option value="ERROR">ERROR</option>
							<option value="WARNING">WARNING</option>
						</select>
					</div>
				</div>
			</div>

			<div className={styles.section}>
				<h3 className={styles.sectionTitle}>ログマッチング設定</h3>

				<div className={styles.row}>
					<div className={styles.field}>
						<label className={styles.label}>Match Method *</label>
						<select
							className={styles.select}
							{...register("logMatchMethod")}
							disabled={isSubmitting}
						>
							<option value="PARTIAL">Partial (部分一致)</option>
							<option value="REGEX">Regex (正規表現)</option>
						</select>
					</div>

					<div className={styles.field}>
						<label className={styles.label}>Log Module Name *</label>
						<select
							className={styles.select}
							{...register("logModuleName")}
							disabled={isSubmitting}
						>
							{logModuleNameValues.map((value) => (
								<option key={value} value={value}>
									{value}
								</option>
							))}
						</select>
					</div>
				</div>

				<div className={styles.field}>
					<label className={styles.label}>Log Keyword *</label>
					<input
						type="text"
						className={`${styles.input} ${errors.logKeyword ? styles.inputError : ""}`}
						{...register("logKeyword")}
						placeholder="例: connection timeout"
						disabled={isSubmitting}
					/>
					{errors.logKeyword && (
						<span className={styles.errorMessage}>
							{errors.logKeyword.message}
						</span>
					)}
				</div>

				<div className={styles.row}>
					<div className={styles.field}>
						<label className={styles.label}>Log Salon IDs (任意)</label>
						<input
							type="text"
							className={styles.input}
							{...register("logSalonIds")}
							placeholder="例: 123, 456, 789（カンマ区切り）"
							disabled={isSubmitting}
						/>
						<span className={styles.hint}>
							複数指定する場合はカンマ区切り (半角空白は許容)
						</span>
					</div>

					<div className={styles.field}>
						<label className={styles.label}>Log Kind (任意)</label>
						<input
							type="text"
							className={styles.input}
							{...register("logKind")}
							placeholder="例: NotFoundException"
							disabled={isSubmitting}
						/>
					</div>
				</div>
			</div>

			<div className={styles.section}>
				<h3 className={styles.sectionTitle}>通知設定</h3>

				<div className={styles.field}>
					<label className={styles.label}>Slack Channel *</label>
					<input
						type="text"
						className={`${styles.input} ${errors.slackChannelNameToNotify ? styles.inputError : ""}`}
						{...register("slackChannelNameToNotify")}
						placeholder="例: 02_dev_alerts"
						disabled={isSubmitting}
					/>
					{errors.slackChannelNameToNotify && (
						<span className={styles.errorMessage}>
							{errors.slackChannelNameToNotify.message}
						</span>
					)}
				</div>

				<div className={styles.field}>
					<label className={styles.label}>Mention Targets (任意)</label>
					<Controller
						name="mentionTargetIds"
						control={control}
						render={({ field }) => (
							<div className={styles.checkboxGroup}>
								{mentionTargets.length === 0 ? (
									<span className={styles.noData}>
										メンション対象がありません
									</span>
								) : (
									mentionTargets.map((target) => (
										<label key={target.id} className={styles.checkboxLabel}>
											<input
												type="checkbox"
												checked={field.value?.includes(target.id) ?? false}
												onChange={(e) => {
													const newValue = e.target.checked
														? [...(field.value ?? []), target.id]
														: (field.value ?? []).filter(
																(id) => id !== target.id
															)
													field.onChange(newValue)
												}}
												disabled={isSubmitting}
											/>
											<span>{target.name}</span>
										</label>
									))
								)}
							</div>
						)}
					/>
				</div>

				<div className={styles.field}>
					<label className={styles.label}>Threshold Count *</label>
					<input
						type="number"
						className={`${styles.input} ${errors.thresholdCountToNotify ? styles.inputError : ""}`}
						{...register("thresholdCountToNotify", { valueAsNumber: true })}
						min={1}
						disabled={isSubmitting}
					/>
					{errors.thresholdCountToNotify && (
						<span className={styles.errorMessage}>
							{errors.thresholdCountToNotify.message}
						</span>
					)}
					<span className={styles.hint}>
						直近24時間で何回発生したら通知するか
					</span>
				</div>

				<div className={styles.field}>
					<Controller
						name="enableCooldown"
						control={control}
						render={({ field }) => (
							<>
								<label className={styles.checkboxLabel}>
									<input
										type="checkbox"
										checked={field.value}
										onChange={field.onChange}
										disabled={isSubmitting}
									/>
									<span>クールダウンを設定する</span>
								</label>
								{field.value && (
									<div className={styles.cooldownInput}>
										<label className={styles.label}>Cooldown Hours *</label>
										<input
											type="number"
											className={`${styles.input} ${errors.cooldownHoursToNotify ? styles.inputError : ""}`}
											{...register("cooldownHoursToNotify", {
												valueAsNumber: true,
											})}
											min={1}
											disabled={isSubmitting}
										/>
										{errors.cooldownHoursToNotify && (
											<span className={styles.errorMessage}>
												{errors.cooldownHoursToNotify.message}
											</span>
										)}
										<span className={styles.hint}>通知間隔（時間）</span>
									</div>
								)}
								{!field.value && (
									<span className={styles.hint}>
										クールダウンなし（条件を満たしたら常に通知）
									</span>
								)}
							</>
						)}
					/>
				</div>

				<div className={styles.row}>
					<div className={styles.field}>
						<label className={styles.label}>Auto Resolve Days *</label>
						<input
							type="number"
							className={`${styles.input} ${errors.autoResolveDaysAfterLastActivity ? styles.inputError : ""}`}
							{...register("autoResolveDaysAfterLastActivity", {
								valueAsNumber: true,
							})}
							min={1}
							disabled={isSubmitting}
						/>
						{errors.autoResolveDaysAfterLastActivity && (
							<span className={styles.errorMessage}>
								{errors.autoResolveDaysAfterLastActivity.message}
							</span>
						)}
						<span className={styles.hint}>
							最終アクティビティ(更新orアラート)から自動解決までの日数
						</span>
					</div>
				</div>

				<div className={styles.field}>
					<label className={styles.checkboxLabel}>
						<input
							type="checkbox"
							{...register("shouldSendIosCriticalNotification")}
							disabled={isSubmitting}
						/>
						<span>iOS Critical Notification を送信する</span>
					</label>
				</div>
			</div>

			<div className={styles.actions}>
				<button
					type="button"
					className={styles.cancelButton}
					onClick={onCancel}
					disabled={isSubmitting}
				>
					キャンセル
				</button>
				<button
					type="submit"
					className={styles.submitButton}
					disabled={isSubmitting}
				>
					{isSubmitting ? "処理中..." : submitLabel}
				</button>
			</div>
		</form>
	)
}

export default KnownIssueForm
